import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, ClipboardList, FileSignature, ClipboardCheck,
  Receipt, Calendar, Pill, Shield, Send, TrendingUp, Video, Building2, UserCheck,
  Megaphone, Settings as SettingsIcon, Moon, Sun, LogOut, Menu, Plus, ChevronLeft, MoreHorizontal,
  CreditCard, FolderArchive, Upload, FormInput, ShieldCheck, ScrollText, Smartphone, Gift, KeyRound, MessageSquare, BarChart3, PenLine
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import SageChat from "./SageChat";
import QuickAdd from "./QuickAdd";
import { PwaStatus } from "./PwaStatus";
import { NotificationBell } from "./NotificationBell";

const sections = [
  { section: "Overview", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/clients", label: "Clients", icon: Users },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/documents", label: "Documents", icon: FolderArchive },
  ]},
  { section: "Clinical", items: [
    { to: "/notes", label: "AI Notes", icon: FileText },
    { to: "/treatment-plans", label: "Treatment Plans", icon: ClipboardList },
    { to: "/intake", label: "Intake", icon: ClipboardCheck },
    { to: "/forms", label: "Forms Library", icon: FormInput },
    { to: "/safety-plans", label: "Safety Plans", icon: Shield },
    { to: "/medications", label: "Medications", icon: Pill },
    { to: "/referrals", label: "Referrals", icon: Send },
    { to: "/outcomes", label: "Outcomes", icon: TrendingUp },
  ]},
  { section: "Operations", items: [
    { to: "/scheduling", label: "Scheduling", icon: Calendar },
    { to: "/insurance", label: "Insurance", icon: CreditCard },
    { to: "/billing", label: "Billing", icon: Receipt },
    { to: "/prior-auth", label: "Prior Auth", icon: FileSignature },
    { to: "/telehealth", label: "Telehealth", icon: Video },
  ]},
  { section: "Growth", items: [
    { to: "/eap", label: "EAP Portal", icon: Building2 },
    { to: "/supervision", label: "Supervision", icon: UserCheck },
    { to: "/marketing", label: "Marketing", icon: Megaphone },
    { to: "/referral-rewards", label: "Referral Rewards", icon: Gift },
  ]},
  { section: "Admin", items: [
    { to: "/merge", label: "Merge Tool", icon: Upload },
    { to: "/privacy", label: "Privacy & AI", icon: ShieldCheck },
    { to: "/mfa", label: "Two-Factor Auth", icon: Smartphone },
    { to: "/audit-log", label: "Audit Log", icon: ScrollText },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
  ]},
];

const adminSection = { section: "Practice Admin", items: [
  { to: "/admin/stats", label: "Website Stats", icon: BarChart3 },
  { to: "/admin/site", label: "Site Editor", icon: PenLine },
  { to: "/admin/temp-access", label: "Temporary Access", icon: KeyRound },
]};

const mobileNav = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/notes", label: "Notes", icon: FileText },
  { to: "/scheduling", label: "Schedule", icon: Calendar },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const navSections = user?.role === "admin" ? [...sections, adminSection] : sections;

  return (
    <div className="min-h-screen flex" style={{ background: "var(--mf-bg)" }}>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col ${collapsed ? "w-20" : "w-64"} transition-all duration-300 py-6 px-3`}
        style={{ background: "var(--mf-surface)" }}
        data-testid="sidebar"
      >
        <div className={`flex ${collapsed ? "flex-col gap-3" : "justify-between px-3"} items-center mb-8`}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 shrink-0 rounded-xl flex items-center justify-center" style={{ background: "var(--mf-primary)" }}>
                <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
              </div>
              <span className="font-serif text-xl" style={{ color: "var(--mf-primary)" }}>Clinician Well</span>
            </div>
          )}
          {collapsed && (
            <div className="w-8 h-8 shrink-0 rounded-xl flex items-center justify-center mx-auto" style={{ background: "var(--mf-primary)" }}>
              <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded-lg hover:bg-black/5"
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} style={{ color: "var(--mf-text-muted)" }} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto space-y-5 pr-1">
          {navSections.map((sec) => (
            <div key={sec.section}>
              {!collapsed && <div className="px-3 text-xs uppercase tracking-wider mb-2" style={{ color: "var(--mf-text-muted)" }}>{sec.section}</div>}
              <div className="space-y-0.5">
                {sec.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => `mf-sidebar-item ${isActive ? "active" : ""}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="pt-4 space-y-1 border-t" style={{ borderColor: "var(--mf-muted)" }}>
          <button onClick={toggle} className="mf-sidebar-item w-full" data-testid="theme-toggle">
            {theme === "dark" ? <Sun className="w-[18px] h-[18px]" strokeWidth={1.75} /> : <Moon className="w-[18px] h-[18px]" strokeWidth={1.75} />}
            {!collapsed && <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>}
          </button>
          <button onClick={logout} className="mf-sidebar-item w-full" data-testid="logout-btn">
            <LogOut className="w-[18px] h-[18px]" strokeWidth={1.75} />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="cw-safe-top flex items-center justify-between px-4 md:px-8 py-3 md:py-4 sticky top-0 z-20" style={{ background: "var(--mf-bg)", backdropFilter: "blur(8px)" }}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded-lg"
              style={{ background: "var(--mf-surface)" }}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />
            </button>
            <div className="md:hidden flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "var(--mf-primary)" }}>
                <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
              </div>
              <span className="font-serif text-lg" style={{ color: "var(--mf-primary)" }}>Clinician Well</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full text-sm" style={{ background: "var(--mf-surface)" }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ background: "var(--mf-primary)" }}>
                {user?.full_name?.[0] || "U"}
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-medium" style={{ color: "var(--mf-text)" }}>{user?.full_name}</div>
                <div className="text-xs capitalize" style={{ color: "var(--mf-text-muted)" }}>{user?.role}</div>
              </div>
            </div>
          </div>
        </header>

        <PwaStatus />
        {user?.is_temporary && user?.access_expires_at && (
          <div className="mx-4 md:mx-8 mb-2 px-4 py-2 rounded-xl text-xs flex items-center gap-2" style={{ background: "rgba(201,168,76,0.14)", color: "#8a6f20" }} data-testid="temp-access-banner">
            <KeyRound className="w-3.5 h-3.5" /> Temporary access · expires {new Date(user.access_expires_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 px-4 md:px-8 pt-2 md:pt-0 pb-40 md:pb-8 overflow-x-hidden">
          {children}
        </div>

        {/* Mobile bottom nav */}
        <nav className="cw-bottom-nav md:hidden fixed bottom-0 left-0 right-0 flex items-center justify-around pt-2 z-30" data-testid="mobile-bottom-nav" style={{ background: "var(--mf-surface)", boxShadow: "0 -4px 20px rgba(0,0,0,0.06)" }}>
          {mobileNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `flex flex-col items-center justify-center py-1 px-3 rounded-lg min-w-[56px] min-h-[44px] ${isActive ? "text-[--mf-primary]" : ""}`}
              style={({ isActive }) => ({ color: isActive ? "var(--mf-primary)" : "var(--mf-text-muted)" })}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.75} />
              <span className="text-[11px] mt-0.5">{item.label}</span>
            </NavLink>
          ))}
          <button
            onClick={() => setMobileOpen(true)}
            className="flex flex-col items-center justify-center py-1 px-3 rounded-lg min-w-[56px] min-h-[44px]"
            style={{ color: mobileOpen ? "var(--mf-primary)" : "var(--mf-text-muted)" }}
            data-testid="mobile-nav-more"
          >
            <MoreHorizontal className="w-5 h-5" strokeWidth={1.75} />
            <span className="text-[11px] mt-0.5">More</span>
          </button>
        </nav>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50" onClick={() => setMobileOpen(false)} data-testid="mobile-drawer">
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="cw-safe-top absolute left-0 top-0 bottom-0 w-[85vw] max-w-xs p-5 pb-10 overflow-y-auto mf-fade-in" style={{ background: "var(--mf-surface)" }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "var(--mf-primary)" }}>
                  <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
                </div>
                <span className="font-serif text-xl" style={{ color: "var(--mf-primary)" }}>Clinician Well</span>
              </div>
              {navSections.map((sec) => (
                <div key={sec.section} className="mb-4">
                  <div className="px-3 text-xs uppercase tracking-wider mb-2" style={{ color: "var(--mf-text-muted)" }}>{sec.section}</div>
                  {sec.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `mf-sidebar-item ${isActive ? "active" : ""}`}
                      data-testid={`mobile-nav-drawer-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
              <button onClick={toggle} className="mf-sidebar-item w-full" data-testid="mobile-theme-toggle">
                {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
                <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
              </button>
              <button onClick={logout} className="mf-sidebar-item w-full" data-testid="mobile-logout">
                <LogOut className="w-[18px] h-[18px]" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        )}

        {/* Floating widgets */}
        <SageChat />
        <QuickAdd onNavigate={navigate} />
      </main>
    </div>
  );
}
